import { ICotizacionValues } from '../../../Providers/CotizacionStore';
import { IEmisionValues } from '../../../Providers/EmisionStore';
import { idDeProvincia } from './catalogoProvincias';
import * as bff from './endpoints';
import { aFechaIso, aSexo } from './funnel';
import { leerSesionCotizacion } from './quoteSession';
import {
  BffEmitirRequest,
  BffEstadoEmision,
  BffGnc,
  BffInspeccion,
  BffPolizaResponse,
  MAX_CILINDROS_GNC,
  Ramo,
  TipoCuentaBancaria,
} from './types';

/**
 * Armado del body de `POST /{ramo}/v1/cotizaciones/{id}/emitir` (§3.5) e
 * idempotencia del intento de emisión.
 */

/** Movido a `bff/idempotencia.ts`; se re-exporta para no tocar los imports de auto. */
export { nuevaIdempotencyKey } from '../idempotencia';

/** "Juan Carlos Perez" -> {nombre:"Juan Carlos", apellido:"Perez"}. */
export const separarTitular = (
  titular?: string
): { nombre: string; apellido: string } => {
  const partes = (titular || '').trim().split(/\s+/).filter(Boolean);
  if (partes.length === 0) return { nombre: '', apellido: '' };
  if (partes.length === 1) return { nombre: partes[0], apellido: '' };
  return {
    nombre: partes.slice(0, -1).join(' '),
    apellido: partes[partes.length - 1],
  };
};

const soloDigitos = (valor?: string): string => (valor || '').replace(/\D/g, '');

const texto = (valor?: string): string | undefined => {
  const limpio = (valor || '').trim();
  return limpio ? limpio : undefined;
};

/**
 * Edad cumplida a partir de una fecha ISO. El BFF la valida contra
 * `fechaNacimiento` con tolerancia de ±1 año, así que no hay input: se calcula
 * y se manda, que es lo que hacía el formulario del monolito.
 */
export const edadDesdeFechaIso = (
  fechaIso?: string,
  hoy: Date = new Date()
): number | undefined => {
  if (!fechaIso) return undefined;
  const partes = fechaIso.split('-').map((parte) => parseInt(parte, 10));
  if (partes.length !== 3 || partes.some((parte) => Number.isNaN(parte))) {
    return undefined;
  }
  const [anio, mes, dia] = partes;
  let edad = hoy.getFullYear() - anio;
  const cumplioEsteAnio =
    hoy.getMonth() + 1 > mes ||
    (hoy.getMonth() + 1 === mes && hoy.getDate() >= dia);
  if (!cumplioEsteAnio) edad -= 1;
  return edad > 0 && edad < 120 ? edad : undefined;
};

/**
 * Vencimiento de la oblea de GNC: tiene que ser una fecha futura y a menos de
 * un año (misma regla que valida el BFF, §3.5). Devuelve `true` o el mensaje
 * de error, que es lo que espera `react-hook-form`.
 */
export const validarVencimientoOblea = (
  fechaIso?: string,
  hoy: Date = new Date()
): true | string => {
  if (!fechaIso) return true;
  const fecha = new Date(`${fechaIso}T00:00:00`);
  if (Number.isNaN(fecha.getTime())) return 'Ingresá una fecha válida';
  const inicioDeHoy = new Date(
    hoy.getFullYear(),
    hoy.getMonth(),
    hoy.getDate()
  );
  if (fecha <= inicioDeHoy) return 'El vencimiento tiene que ser futuro';
  const tope = new Date(inicioDeHoy);
  tope.setFullYear(tope.getFullYear() + 1);
  if (fecha > tope) return 'El vencimiento no puede superar un año';
  return true;
};

/**
 * Detalle de GNC -> bloque `vehiculo.gnc`. Devuelve null cuando el vehículo no
 * tiene GNC o cuando no se cargó ningún dato: el bloque es opcional aunque
 * `tieneGnc` sea true.
 */
export const construirGnc = (emision: IEmisionValues): BffGnc | null => {
  if (!emision.gnc) return null;
  const detalle = emision.gncDetalle || {};
  const cilindros = (detalle.cilindros || [])
    .map((cilindro) => ({
      marca: texto(cilindro.marca),
      numero: texto(cilindro.numero),
    }))
    .filter((cilindro) => cilindro.marca || cilindro.numero)
    .slice(0, MAX_CILINDROS_GNC);

  const gnc: BffGnc = {
    regulador: texto(detalle.marcaRegulador),
    numeroRegulador: texto(detalle.numeroRegulador),
    numeroOblea: texto(detalle.numeroOblea),
    vencimientoOblea: texto(detalle.vencimientoOblea),
    cilindros: cilindros.length ? cilindros : undefined,
  };
  const tieneAlgo = Object.values(gnc).some((valor) => valor !== undefined);
  return tieneAlgo ? gnc : null;
};

/**
 * Inspección del vehículo -> bloque `vehiculo.inspeccion`. Sin tipo elegido no
 * hay bloque: el BFF sigue aceptando el `tipoInspeccion` suelto.
 */
export const construirInspeccion = (
  emision: IEmisionValues
): BffInspeccion | null => {
  const inspeccion = emision.inspeccion;
  const tipo = texto(inspeccion?.tipo);
  if (!tipo) return null;

  const esADomicilio = tipo.toUpperCase() === 'A-DOMICILIO';
  const dom = inspeccion?.domicilio;
  return {
    tipo,
    domicilio:
      esADomicilio && dom
        ? {
            provinciaId: (dom.provinciaId || '').toString().trim(),
            localidad: (dom.localidad || '').trim(),
            codigoPostal: (dom.codigoPostal || '').trim(),
            calle: (dom.calle || '').trim(),
            altura: (dom.altura || '').trim(),
            fecha: texto(dom.fecha),
            horarioDesde: texto(dom.horarioDesde),
            horarioHasta: texto(dom.horarioHasta),
            codigoArea: texto(dom.codigoArea)?.replace(/^0+/, ''),
            telefono: dom.telefono ? soloDigitos(dom.telefono) : undefined,
          }
        : null,
    centroCompania: texto(inspeccion?.centroCompania) ?? null,
    ciaCodigoArea: texto(inspeccion?.ciaCodigoArea) ?? null,
    ciaTelefono: texto(inspeccion?.ciaTelefono) ?? null,
  };
};

/**
 * Stores del front -> DTO de emisión del BFF.
 *
 * El PAN de la tarjeta TRANSITA en este body (el BFF lo enmascara en logs y no
 * lo persiste, §3.5): no se guarda en el store más de lo que ya se guardaba ni
 * se loguea acá.
 */
export const construirEmitirRequest = (
  emision: IEmisionValues,
  cotizacion: ICotizacionValues,
  opciones: {
    esCeroKm?: boolean;
    tipoInspeccion?: string | null;
    /**
     * Débito por CBU. Hoy no hay pantalla que lo cargue (el único medio es
     * tarjeta de crédito), pero el `tipoCuenta` es obligatorio para el BFF
     * cuando el medio es CBU, así que el armado queda hecho.
     */
    cbu?: {
      numero: string;
      banco: string;
      tipoCuenta: TipoCuentaBancaria;
    } | null;
  } = {}
): BffEmitirRequest => {
  const titular = separarTitular(emision.titularTarjeta);
  const provinciaId = idDeProvincia(cotizacion.provincia?.value);
  const fechaNacimiento = aFechaIso({
    dia: emision.fechaNac?.dia ?? cotizacion.fechaNac?.dia,
    mes: emision.fechaNac?.mes ?? cotizacion.fechaNac?.mes,
    año: emision.fechaNac?.anio ?? cotizacion.fechaNac?.año,
  });
  const inspeccion = construirInspeccion(emision);
  const pagoPorCbu = opciones.cbu || null;

  return {
    tomador: {
      tipoPersona:
        (emision.tipoPersona?.value || 'FISICA').toUpperCase() === 'JURIDICA'
          ? 'JURIDICA'
          : 'FISICA',
      condicionIva: emision.condicionIva?.value || '',
      tipoDocumento: 'DNI',
      numeroDocumento: soloDigitos(emision.numDoc),
      cuil: soloDigitos(emision.cuil),
      nombre: (emision.nombre || '').trim(),
      apellido: (emision.apellido || '').trim(),
      razonSocial: null,
      fechaNacimiento,
      // No hay input de edad: se calcula de la fecha de nacimiento y el BFF la
      // cruza con ella (tolerancia ±1 año).
      edad: edadDesdeFechaIso(fechaNacimiento),
      sexo: aSexo(cotizacion.genero?.value),
      email: (emision.email || cotizacion.email || '').trim().toLowerCase(),
      telefono: {
        codigoArea: (emision.celular?.cod || '').replace(/^0+/, ''),
        numero: soloDigitos(emision.celular?.numero),
        esCelular: true,
      },
      telefonoAdicional:
        emision.telAdicional?.cod && emision.telAdicional?.numero
          ? {
              codigoArea: emision.telAdicional.cod.replace(/^0+/, ''),
              numero: soloDigitos(emision.telAdicional.numero),
              esCelular: true,
            }
          : null,
      domicilio: {
        calle: (emision.calle || '').trim(),
        numero: emision.altura?.trim() ? emision.altura.trim() : 'S/N',
        piso: emision.piso || '',
        departamento: emision.departamento || '',
        provinciaId,
        localidad: (emision.localidad || '').trim(),
        codigoPostal: (emision.codigoPostal || cotizacion.codigoPostal || '')
          .toString()
          .trim(),
      },
    },
    vehiculo: {
      // Los nombres salen del cotizador: la emisión no los vuelve a pedir.
      marcaNombre: (cotizacion.marca?.value || '').trim(),
      modeloNombre: (cotizacion.modelo?.value || '').trim(),
      versionNombre: (cotizacion.version?.value || '').trim(),
      patente: (emision.patente || '').trim().toUpperCase(),
      sinPatente: !emision.patente,
      numeroMotor: (emision.numMotor || '').trim(),
      numeroChasis: (emision.numChasis || '').trim(),
      tieneGnc: !!emision.gnc,
      gnc: construirGnc(emision),
      tipoUso: emision.tipoUso?.value || 'PARTICULAR',
      // Se sigue mandando la forma vieja; el BFF le da prioridad a `inspeccion.tipo`.
      tipoInspeccion: inspeccion?.tipo ?? opciones.tipoInspeccion ?? null,
      inspeccion,
      esCeroKm: !!opciones.esCeroKm,
      // Solo si la compañía del plan la exige (US 24094): para el resto no se pregunta ni viaja.
      vtv: emision.cotizacionDesc?.vtvRequerida ? texto(emision.vtv) ?? null : null,
    },
    pago: {
      medio: pagoPorCbu ? 'CBU' : 'TARJETA_CREDITO',
      tarjeta: pagoPorCbu
        ? null
        : {
        numero: soloDigitos(emision.numeroTarjeta),
        tipo: emision.tipoTarjeta?.value || '',
        // El monolito mandaba el LABEL del banco; el BFF pide el código.
        banco: emision.banco?.value || '',
        titularNombre: titular.nombre,
        titularApellido: titular.apellido,
        vencimientoMes: parseInt(emision.vencimientoTarjeta?.mes || '0', 10),
        vencimientoAnio: normalizarAnioVencimiento(
          emision.vencimientoTarjeta?.anio
        ),
      },
      cbu: pagoPorCbu
        ? {
            numero: soloDigitos(pagoPorCbu.numero),
            banco: pagoPorCbu.banco,
            tipoCuenta: pagoPorCbu.tipoCuenta,
          }
        : null,
    },
    aceptaTerminos: true,
  };
};

/** "30" -> 2030; "2030" -> 2030. */
export const normalizarAnioVencimiento = (anio?: string): number => {
  const digitos = soloDigitos(anio);
  if (!digitos) return 0;
  const numero = parseInt(digitos, 10);
  if (digitos.length <= 2) return 2000 + numero;
  return numero;
};

/**
 * Emite. La `Idempotency-Key` se genera UNA vez por intento y se reusa en los
 * reintentos, que es lo que evita la póliza duplicada tras un 504 o un 409.
 */
export const emitir = (
  ramo: Ramo,
  body: BffEmitirRequest,
  idempotencyKey: string
): Promise<BffPolizaResponse> => {
  const { cotizacionId } = leerSesionCotizacion();
  if (!cotizacionId) {
    return Promise.reject(new Error('No hay cotización activa para emitir.'));
  }
  return bff.postEmitir(ramo, cotizacionId, body, idempotencyKey);
};

/** Estado de la póliza ya emitida. */
export const consultarPoliza = (
  ramo: Ramo,
  polizaId: string
): Promise<BffPolizaResponse> => bff.getPoliza(ramo, polizaId);

/** Estado de la emisión de una cotización (none | issuing | issued | failed). */
export const consultarEstadoEmision = (
  ramo: Ramo,
  cotizacionId: string
): Promise<BffEstadoEmision> => bff.getEstadoEmision(ramo, cotizacionId);

export const ESPERA_POLLING_POR_DEFECTO_S = 5;
export const INTENTOS_POLLING_POR_DEFECTO = 12;

const dormir = (segundos: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, Math.max(1, segundos) * 1000);
  });

/**
 * Espera a que la emisión se resuelva, consultando
 * `GET cotizaciones/{id}/emision`.
 *
 * Es lo que se usa después de un 409 `issue_in_progress` o un 504
 * `provider_timeout`: el intento ya salió, no hay que pedirle a la persona que
 * vuelva a apretar Contratar (eso arriesga una póliza duplicada).
 *
 * Corta cuando el estado deja de ser `issuing`, cuando el BFF avisa que la
 * emisión quedó colgada (`requiereAsistencia`, o `retryAfterSeconds: null`) o
 * cuando se agotan los intentos; en ese caso devuelve el último estado
 * conocido.
 */
export const esperarEmision = async (
  ramo: Ramo,
  cotizacionId: string,
  opciones: {
    esperaInicialS?: number;
    intentos?: number;
    /** Avisa cada vez que hay un estado nuevo, para mover la UI. */
    onEstado?: (estado: BffEstadoEmision) => void;
    /** Permite cancelar el polling al desmontar la pantalla. */
    seguirEsperando?: () => boolean;
  } = {}
): Promise<BffEstadoEmision> => {
  const intentos = opciones.intentos ?? INTENTOS_POLLING_POR_DEFECTO;
  let espera = opciones.esperaInicialS ?? ESPERA_POLLING_POR_DEFECTO_S;
  let ultimo: BffEstadoEmision = { estado: 'issuing' };

  for (let intento = 0; intento < intentos; intento += 1) {
    if (opciones.seguirEsperando && !opciones.seguirEsperando()) return ultimo;
    // eslint-disable-next-line no-await-in-loop
    await dormir(espera);
    if (opciones.seguirEsperando && !opciones.seguirEsperando()) return ultimo;
    try {
      // eslint-disable-next-line no-await-in-loop
      ultimo = await consultarEstadoEmision(ramo, cotizacionId);
    } catch {
      // Una consulta que falla no corta la espera: puede ser un corte puntual.
      // eslint-disable-next-line no-continue
      continue;
    }
    opciones.onEstado?.(ultimo);
    if (ultimo.estado !== 'issuing') return ultimo;
    // La emisión quedó colgada: seguir consultando no la resuelve y reintentar
    // arriesga un segundo cobro. Se corta y la pantalla avisa.
    if (ultimo.requiereAsistencia || ultimo.retryAfterSeconds === null) {
      return ultimo;
    }
    espera = ultimo.retryAfterSeconds ?? espera;
  }
  return ultimo;
};
